<script setup>
import { computed, ref, watch, onMounted } from "vue";
import { useForm, usePage } from "@inertiajs/inertia-vue3";
import TextInput from "@/Components/TextInput.vue";
import InputError from "@/Components/InputError.vue";
import InputLabel from "@/Components/InputLabel.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import { Inertia } from "@inertiajs/inertia";
import { useHelpers, useTenantButtonVisibility, useTenantTextRules } from "@/helpers";
import NumberInput from "@/Components/NumberInput.vue";
import DisabledInput from "@/Components/DisabledInput.vue";
import Checkbox from "@/Components/Checkbox.vue";
import SelectInput from "@/Components/SelectInput.vue";
import SearchSelect from "@/Components/SearchSelect.vue";

import { debounce } from 'lodash';

const helpers = useHelpers();
const { isButtonVisible, isButtonEnabled } = useTenantButtonVisibility();
const { resolveTextRule } = useTenantTextRules();
const currentUser = usePage().props.value.auth.user;
const projectProperty = computed(() => usePage().props.value.project);
let project = projectProperty.value;
const mapPricing = (project) => {
  return {
    shipping_cost: parseFloat(project?.pricing?.shipping_cost || 0).toFixed(2),
    client_shipping_cost: parseFloat(project?.pricing?.client_shipping_cost || 0).toFixed(2),
    taxes: parseFloat(project?.pricing?.taxes || 0),
    prjquote: parseFloat(project?.pricing?.prjquote || 0).toFixed(2),
    prj_completioncost: parseFloat(project?.pricing?.prj_completioncost || 0).toFixed(2),
    prj_est_profit: parseFloat(project?.pricing?.prj_est_profit || 0).toFixed(2),
  }
}

const mapEstimatedUnitCost = (project) => {
  return {
    ptrnsetup: parseFloat(project?.estimated_unit_cost?.ptrnsetup || 0).toFixed(2),
    grdngsetup: parseFloat(project?.estimated_unit_cost?.grdngsetup || 0).toFixed(2),
    smplefeesetup: parseFloat(project?.estimated_unit_cost?.smplefeesetup || 0).toFixed(2),
    fabric: parseFloat(project?.estimated_unit_cost?.fabric || 0).toFixed(2),
    trimfee: parseFloat(project?.estimated_unit_cost?.trimfee || 0).toFixed(2),
    labour: parseFloat(project?.estimated_unit_cost?.labour || 0).toFixed(2),
    duty: parseFloat(project?.estimated_unit_cost?.duty || 0).toFixed(2),
    frieght: parseFloat(project?.estimated_unit_cost?.frieght || 0).toFixed(2),
    other: parseFloat(project?.estimated_unit_cost?.other || 0).toFixed(2),
  }
}

onMounted(() => {
  syncStyleOtherActiveFromRows();
  calculateBreakdown();
});

watch(projectProperty, (value) => {
  if (Object.keys(usePage().props.value.errors).length > 0) {
    return;
  }
  form.styles = value.styles;
  syncStyleOtherActiveFromRows();
});

const props = defineProps({
  taxes: Array,
  pricingSkuOptions: {
    type: Array,
    default() {
      return [];
    }
  },
  pricingSizeScaleOptions: {
    type: Array,
    default() {
      return [];
    }
  },
  pricingQuantityOptions: {
    type: Array,
    default() {
      return [];
    }
  },
  isGarmentColorPricingView: {
    type: Boolean,
    default: false,
  },
  pricingEquipmentRates: {
    type: Object,
    default() {
      return {};
    }
  },
  pricingRentalPeriodOptions: {
    type: Array,
    default() {
      return [];
    }
  },
  hideSave: {
    type: Boolean,
    default() {
      return false;
    }
  }
});

const saving = ref(false);
const form = useForm({
  _method: 'PUT',
  styles: project.styles,
  pricing: mapPricing(project),
  estimated_unit_cost: mapEstimatedUnitCost(project),
});

const rentalPriceDependencySignature = computed(() => JSON.stringify(
  form.styles.map((row) => [
    row?.style ?? null,
    row?.vendor_style ?? null,
    row?.garments ?? null,
  ])
));

const saveForm = () => {
  form.post(route('admin.projects.update', { project: project.pid }), {
    preserveScroll: true,
    onFinish() {
      saving.value = false;
    },
    onError() {
      saving.value = false;
    }
  });
}

watch(rentalPriceDependencySignature, debounce(() => {
  if (!props.isGarmentColorPricingView) {
    return;
  }

  applyDefaultRentalPrices();
  calculateBreakdown();
}, 200));

watch(() => form.styles, debounce(() => {
  calculateBreakdown();
}, 200), { deep: true });

watch(() => form.pricing.taxes, () => {
  console.log('form.pricing.taxes changed');
  calculateBreakdown();
});

watch(() => form.pricing.shipping_cost, debounce(() => {
  console.log('form.pricing.shipping_cost changed');
  calculateBreakdown();
}, 500));

watch(() => form.pricing.client_shipping_cost, debounce(() => {
  console.log('form.pricing.client_shipping_cost changed');
  calculateBreakdown();
}, 500));

// old calculateBreakdown function
/*const calculateBreakdown = () => {
  console.log("calculate breakdown");
  let hiddenCost = 0;
  let targetTotal = 0;

  // Calculate hidden cost and target total from styles
  form.styles.forEach(row => {
    hiddenCost += parseFloat(row.priceunit || 0) * parseInt(row.garments || 0);
    targetTotal += parseFloat(row.retailprice || 0) * parseInt(row.garments || 0);
  });

  // Calculate total with tax
  const targetTotalWithTax = targetTotal + (targetTotal * parseFloat(form.pricing.taxes || 0));

  // Purchase Order Total: Reverting back to previous calculation
  form.pricing.prjquote = (targetTotalWithTax + parseFloat(form.pricing.client_shipping_cost || 0)).toFixed(2);

  // Project Completion Cost: Actual Shipping Cost only
  form.pricing.prj_completioncost = (hiddenCost + parseFloat(form.pricing.shipping_cost || 0)).toFixed(2);

  // Base Profit: Difference between sales and costs (excluding shipping)
  const baseProfit = targetTotalWithTax - hiddenCost;

  // Project Estimated Profit: Base profit plus difference between billed and actual shipping
  form.pricing.prj_est_profit = (
    baseProfit +
    (parseFloat(form.pricing.client_shipping_cost || 0) - parseFloat(form.pricing.shipping_cost || 0))
  ).toFixed(2);

  // Debugging outputs
  //console.log("prjquote:", form.pricing.prjquote);
  //console.log("prj_completioncost:", form.pricing.prj_completioncost);
  //console.log("prj_est_profit:", form.pricing.prj_est_profit);
};*/

const RENTAL_PERIOD_RATE_RULES = [
  { label: 'Daily', value: 'Daily', rateKey: 'daily', termKey: 'project.pricing.rental_period.daily' },
  { label: 'Weekly', value: 'Weekly', rateKey: 'weekly', termKey: 'project.pricing.rental_period.weekly' },
  { label: 'Monthly', value: 'Monthly', rateKey: 'monthly', termKey: 'project.pricing.rental_period.monthly' },
];

const rentalRateKeyFromPeriodLabel = (label) => {
  const normalized = `${label || ''}`.trim().toLowerCase();

  if (!normalized) {
    return null;
  }

  const matched = RENTAL_PERIOD_RATE_RULES.find((option) => {
    const ruleLabel = `${resolveTextRule(option.termKey, option.label, option.termKey) || ''}`.trim().toLowerCase();

    return (
      normalized === option.value.toLowerCase()
      || normalized === option.rateKey
      || (ruleLabel !== '' && normalized === ruleLabel)
    );
  });

  return matched?.rateKey ?? null;
};

const calculateDefaultRentalPrice = (row) => {
  const styleKey = `${row?.style || ''}`.trim();
  const periodLabel = `${row?.vendor_style || ''}`.trim();
  const days = parseInt(row?.garments || 0, 10);

  if (!styleKey || !periodLabel || !days || days <= 0) {
    return null;
  }

  const rates = props.pricingEquipmentRates?.[styleKey];
  const rateKey = rentalRateKeyFromPeriodLabel(periodLabel);

  if (!rates || !rateKey) {
    return null;
  }

  const rate = parseFloat(rates[rateKey] ?? 0);

  if (Number.isNaN(rate)) {
    return null;
  }

  return (rate * days).toFixed(2);
};

const applyDefaultRentalPrices = () => {
  if (!props.isGarmentColorPricingView) {
    return;
  }

  form.styles.forEach((row) => {
    const calculated = calculateDefaultRentalPrice(row);

    if (calculated !== null) {
      row.retailprice = calculated;
    }
  });
};

const calculateBreakdown = () => {
  console.log("calculate breakdown");
  let hiddenCost = 0;
  let targetTotal = 0;
  let targetTaxableTotal = 0;

  form.styles.forEach(row => {
    const garments = parseInt(row.garments || 0);
    const priceUnit = parseFloat(row.priceunit || 0);
    const retailPrice = parseFloat(row.retailprice || 0);

    if (props.isGarmentColorPricingView) {
      hiddenCost += priceUnit;
      targetTotal += retailPrice;

      if (row.is_taxable) {
        targetTaxableTotal += retailPrice;
      }
    } else {
      hiddenCost += priceUnit * garments;
      targetTotal += retailPrice * garments;

      if (row.is_taxable) {
        targetTaxableTotal += retailPrice * garments;
      }
    }
  });

  const taxRate = parseFloat(form.pricing.taxes || 0);
  const taxAmount = targetTaxableTotal * taxRate;
  const targetTotalWithTax = targetTotal + taxAmount;

  form.pricing.prjquote = (targetTotalWithTax + parseFloat(form.pricing.client_shipping_cost || 0)).toFixed(2);

  form.pricing.prj_completioncost = (hiddenCost + parseFloat(form.pricing.shipping_cost || 0)).toFixed(2);

  const baseProfit = /*targetTotalWithTax*/ targetTotal - hiddenCost;
  form.pricing.prj_est_profit = (
    baseProfit +
    (parseFloat(form.pricing.client_shipping_cost || 0) - parseFloat(form.pricing.shipping_cost || 0))
  ).toFixed(2);
};


const addNewPricingRow = () => {
  const style = { prj_style_id: null, style: null, vendor_style: null, garments: 0, priceunit: 0, retailprice: 0, is_taxable: true };

  form.styles.push(style);
  setStyleOtherActive(form.styles.length - 1, false);
}

const moveStyle = (index, delta) => {
  const target = index + delta;
  if (target < 0 || target >= form.styles.length) {
    return;
  }

  const styles = [...form.styles];
  const current = styles[index];
  styles[index] = styles[target];
  styles[target] = current;
  form.styles = styles;

  const currentOtherActive = !!styleOtherActive.value[index];
  const targetOtherActive = !!styleOtherActive.value[target];
  styleOtherActive.value = {
    ...styleOtherActive.value,
    [index]: targetOtherActive,
    [target]: currentOtherActive,
  };
};

const deleteRow = (index) => {
  const style = form.styles[index];
  form.styles.splice(index, 1);

  const remapped = {};
  Object.entries(styleOtherActive.value).forEach(([key, active]) => {
    if (!active) {
      return;
    }
    const oldIndex = Number(key);
    if (oldIndex === index) {
      return;
    }
    remapped[oldIndex > index ? oldIndex - 1 : oldIndex] = true;
  });
  styleOtherActive.value = remapped;

  if (style.prj_style_id === null) {
    return;
  }

  saveForm();
  Inertia.post(route('admin.projects.styles.destroy', {
    projectid: project.pid,
    styleid: style.prj_style_id
  }), { _method: 'DELETE' }, {
    preserveScroll: true
  });
}

const showEstimatedBreakdown = ref(false);
const calculateEstimatedUnitCost = () => {
  let estimatedCost = 0;
  Object.keys(form.estimated_unit_cost).forEach(key => {
    estimatedCost += parseFloat(form.estimated_unit_cost[key]);
  });

  return parseFloat(estimatedCost).toFixed(2);
}
const useDatabaseSkuSearchSelect = computed(() =>
  isButtonEnabled('project.edit.pricing.database_sku_select', false)
);
const STYLE_OTHER_VALUE = '__OTHER__';
const styleOtherActive = ref({});
const catalogStyleValues = computed(() =>
  (props.pricingSkuOptions || []).map((option) => `${option?.value ?? ''}`.trim()).filter(Boolean)
);
const isCustomStyleValue = (value) => {
  const normalized = `${value ?? ''}`.trim();

  return normalized !== '' && !catalogStyleValues.value.includes(normalized);
};
const isStyleOtherMode = (index, value) =>
  styleOtherActive.value[index] || isCustomStyleValue(value);
const setStyleOtherActive = (index, active) => {
  styleOtherActive.value = {
    ...styleOtherActive.value,
    [index]: active,
  };
};
const syncStyleOtherActiveFromRows = () => {
  const next = { ...styleOtherActive.value };

  form.styles.forEach((row, index) => {
    if (isCustomStyleValue(row?.style)) {
      next[index] = true;
    }
  });

  styleOtherActive.value = next;
};
const onGarmentColorStyleSelect = (index, value) => {
  if (value === STYLE_OTHER_VALUE) {
    setStyleOtherActive(index, true);
    form.styles[index].style = '';

    return;
  }

  setStyleOtherActive(index, false);
  form.styles[index].style = value;
};
const exitGarmentColorStyleOther = (index) => {
  setStyleOtherActive(index, false);
  form.styles[index].style = null;
};
const pricingSkuOptionsWithOther = (styleValue) => {
  const options = pricingSkuOptionsForStyle(styleValue);

  if (!props.isGarmentColorPricingView) {
    return options;
  }

  const otherLabel = pricingStyleOtherLabel.value;
  const hasOther = options.some((option) => `${option?.value ?? ''}`.trim() === STYLE_OTHER_VALUE);

  if (hasOther) {
    return options;
  }

  return [
    ...options,
    { label: otherLabel, value: STYLE_OTHER_VALUE },
  ];
};
const useDatabaseSizeScaleSelect = computed(() =>
  isButtonEnabled('project.edit.pricing.database_size_scale_select', false)
);
const pricingQuantityOptionsFromProps = computed(() =>
  Array.isArray(props.pricingQuantityOptions) ? props.pricingQuantityOptions : []
);
const pricingQuantityOptionsRaw = computed(() =>
  resolveTextRule(
    'project.pricing.table.quantity.options',
    '',
    'project.pricing.table.quantity.options'
  )
);
const pricingQuantityOptions = computed(() => {
  if (pricingQuantityOptionsFromProps.value.length > 0) {
    return pricingQuantityOptionsFromProps.value;
  }

  const raw = `${pricingQuantityOptionsRaw.value || ''}`.trim();

  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map((option, index) => {
        if (typeof option === 'string') {
          return {
            label: option,
            value: index + 1,
          };
        }

        const label = `${option?.label ?? ''}`.trim();
        const rawValue = option?.value;
        const numericValue = Number(rawValue);

        if (!label || Number.isNaN(numericValue)) {
          return null;
        }

        return {
          label,
          value: numericValue,
        };
      })
      .filter(Boolean);
  } catch (error) {
    return [];
  }
});
const usePricingQuantitySelect = computed(() =>
  !props.isGarmentColorPricingView && pricingQuantityOptions.value.length > 0
);
const useRentalPeriodSelect = computed(() => props.isGarmentColorPricingView);
const pricingSkuOptionsForStyle = (styleValue) => {
  const normalized = `${styleValue || ''}`.trim();
  const options = Array.isArray(props.pricingSkuOptions) ? [...props.pricingSkuOptions] : [];

  if (!normalized) {
    return options;
  }

  const hasMatch = options.some((option) => `${option?.value ?? ''}`.trim() === normalized);

  if (hasMatch) {
    return options;
  }

  return [
    { label: normalized, value: normalized },
    ...options,
  ];
};
const pricingQuantityOptionsForStyle = (quantityValue) => {
  const numericValue = Number(quantityValue);

  if (Number.isNaN(numericValue)) {
    return pricingQuantityOptions.value;
  }

  const hasMatch = pricingQuantityOptions.value.some((option) => Number(option.value) === numericValue);

  if (hasMatch || numericValue <= 0) {
    return pricingQuantityOptions.value;
  }

  return [
    { label: String(quantityValue), value: numericValue },
    ...pricingQuantityOptions.value,
  ];
};
const pricingSizeScaleOptionsForStyle = (styleValue) => {
  const normalized = `${styleValue || ''}`.trim();
  const options = Array.isArray(props.pricingSizeScaleOptions) ? [...props.pricingSizeScaleOptions] : [];

  if (!normalized) {
    return options;
  }

  const hasMatch = options.some((option) => `${option?.value ?? ''}`.trim() === normalized);

  if (hasMatch) {
    return options;
  }

  return [
    { label: normalized, value: normalized },
    ...options,
  ];
};
const rentalPeriodSelectOptions = computed(() => {
  const options = Array.isArray(props.pricingRentalPeriodOptions)
    ? props.pricingRentalPeriodOptions.map((option) => ({
        label: `${option?.label ?? option?.value ?? ''}`.trim(),
        value: `${option?.value ?? option?.label ?? ''}`.trim(),
      })).filter((option) => option.label && option.value)
    : [];

  return options;
});
const pricingRentalPeriodOptionsForStyle = (periodValue) => {
  const normalized = `${periodValue || ''}`.trim();
  const options = rentalPeriodSelectOptions.value;

  if (!normalized) {
    return options;
  }

  const hasMatch = options.some((option) => `${option?.value ?? ''}`.trim() === normalized);

  if (hasMatch) {
    return options;
  }

  return [
    { label: normalized, value: normalized },
    ...options,
  ];
};
const pricingStyleLabel = computed(() =>
  resolveTextRule('project.pricing.table.style', 'Style', 'project.pricing.table.style')
);
const pricingStyleOtherLabel = computed(() =>
  resolveTextRule('project.pricing.style.other', 'Other', 'project.pricing.style.other')
);
const pricingStyleOtherDescriptionLabel = computed(() =>
  resolveTextRule(
    'project.pricing.style.other_description',
    'Enter description',
    'project.pricing.style.other_description'
  )
);
const pricingSizeLabel = computed(() =>
  resolveTextRule('project.pricing.table.size', 'Size', 'project.pricing.table.size')
);
const pricingTargetRetailLabel = computed(() =>
  resolveTextRule(
    'project.pricing.table.target_retail_price',
    props.isGarmentColorPricingView ? 'Rental Price or Sales Price' : 'Target Retail Price',
    'project.pricing.table.target_retail_price'
  )
);
const pricingQuantityLabel = computed(() =>
  resolveTextRule(
    'project.pricing.table.quantity',
    props.isGarmentColorPricingView ? 'Rental Days or Quantity' : '# of Garments',
    'project.pricing.table.quantity'
  )
);
const pricingBillableSampleLabel = computed(() =>
  resolveTextRule('project.pricing.table.billable_sample', 'Billable Sample', 'project.pricing.table.billable_sample')
);
const pricingDeliveryBilledLabel = computed(() =>
  resolveTextRule(
    'project.pricing.table.delivery_pick_up_billed_to_client',
    'Shipping Billed To Client',
    'project.pricing.table.delivery_pick_up_billed_to_client',
  )
);
const isCostPerUnitVisible = computed(() => isButtonVisible('project.edit.pricing.cost_per_unit'));
const isCostBasisColumnVisible = computed(() =>
  props.isGarmentColorPricingView || isCostPerUnitVisible.value
);
const pricingCostColumnLabel = computed(() =>
  props.isGarmentColorPricingView
    ? resolveTextRule('project.pricing.table.cost_basis', 'Cost Basis', 'project.pricing.table.cost_basis')
    : 'Cost Per Unit'
);
</script>

<template>
  <div class="px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md mb-5">
    <table class="min-w-full w-full text-left text-sm font-light" v-if="form.styles.length > 0">
      <thead class="border-b font-medium dark:border-neutral-500">
        <tr>
          <th
            v-if="!hideSave && helpers.userCanManageProject(currentUser)"
            scope="col"
            class="px-2 py-2 w-10"
          ></th>
          <th scope="col" class="px-4 py-2">{{ pricingStyleLabel }}</th>
          <th scope="col" class="px-4 py-2">{{ pricingSizeLabel }}</th>
          <th scope="col" class="px-4 py-2">{{ pricingQuantityLabel }}</th>
          <th scope="col" class="px-4 py-2" v-if="isCostBasisColumnVisible">{{ pricingCostColumnLabel }}</th>
          <th scope="col" class="px-4 py-2" v-if="helpers.userCanManageProject(currentUser)">{{ pricingTargetRetailLabel }}</th>
          <th scope="col" class="px-4 py-2">{{ pricingBillableSampleLabel }}</th>
          <th scope="col" class="px-4 py-2">Taxable</th>
          <th scope="col" class="px-4 py-2">Action</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(style, index) in form.styles"
          class="border-b transition duration-300 ease-in-out hover:bg-neutral-200">
          <td
            v-if="!hideSave && helpers.userCanManageProject(currentUser)"
            class="px-2 py-2 whitespace-nowrap"
          >
            <div class="flex flex-col items-center gap-1">
              <button
                type="button"
                class="text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed"
                title="Move up"
                :disabled="index === 0"
                @click="moveStyle(index, -1)"
              >
                <span class="fa fa-arrow-up"></span>
              </button>
              <button
                type="button"
                class="text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed"
                title="Move down"
                :disabled="index === form.styles.length - 1"
                @click="moveStyle(index, 1)"
              >
                <span class="fa fa-arrow-down"></span>
              </button>
            </div>
          </td>
          <td class="px-4 py-2 break-words">
            <template v-if="useDatabaseSkuSearchSelect && isGarmentColorPricingView">
              <SearchSelect
                v-if="!isStyleOtherMode(index, form.styles[index].style)"
                :modelValue="form.styles[index].style"
                @update:modelValue="onGarmentColorStyleSelect(index, $event)"
                :options="pricingSkuOptionsWithOther(form.styles[index].style)"
                placeholder="Please select"
                :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
                filterMode="includes"
              />
              <div v-else class="relative mt-1">
                <input
                  type="text"
                  v-model="form.styles[index].style"
                  :placeholder="pricingStyleOtherDescriptionLabel"
                  :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
                  class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-lg shadow-sm block w-full py-2 px-3 pr-10 disabled:bg-gray-50 disabled:text-gray-500"
                />
                <button
                  v-if="helpers.userCanManageProject(currentUser) || helpers.userIsVendor(currentUser)"
                  type="button"
                  class="absolute inset-y-0 right-3 flex items-center text-gray-600 hover:text-gray-800"
                  title="Select from list"
                  @click="exitGarmentColorStyleOther(index)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </template>
            <SearchSelect
              v-else-if="useDatabaseSkuSearchSelect"
              v-model="form.styles[index].style"
              :options="pricingSkuOptionsForStyle(form.styles[index].style)"
              placeholder="Please select"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
              filterMode="includes"
            />
            <TextInput
              v-else
              type="text"
              class="mt-1 block w-full"
              v-model="form.styles[index].style"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
            />
          </td>
          <td class="px-4 py-2 break-words">
            <SelectInput
              v-if="useRentalPeriodSelect"
              v-model="form.styles[index].vendor_style"
              class="mt-1 block w-full disabled:bg-gray-50"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
            >
              <option :value="null">Please select</option>
              <option
                v-for="option in pricingRentalPeriodOptionsForStyle(form.styles[index].vendor_style)"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </SelectInput>
            <SearchSelect
              v-else-if="useDatabaseSizeScaleSelect"
              v-model="form.styles[index].vendor_style"
              :options="pricingSizeScaleOptionsForStyle(form.styles[index].vendor_style)"
              placeholder="Please select"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
              filterMode="includes"
            />
            <TextInput
              v-else
              type="text"
              class="mt-1 block w-full"
              v-model="form.styles[index].vendor_style"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
            />
          </td>
          <td class="px-4 py-2 break-words">
            <SelectInput
              v-if="usePricingQuantitySelect"
              v-model="form.styles[index].garments"
              class="mt-1 block w-full disabled:bg-gray-50"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
            >
              <option :value="null">Please select</option>
              <option
                v-for="option in pricingQuantityOptionsForStyle(form.styles[index].garments)"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </SelectInput>
            <NumberInput
              v-else
              step="1"
              class="mt-1 block w-full"
              v-model="form.styles[index].garments"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
            />
          </td>
          <td class="px-4 py-2 break-words" v-if="isCostBasisColumnVisible">
            <TextInput type="number" class="mt-1 block w-full" v-model="form.styles[index].priceunit"
              :disabled="(!helpers.userCanManageProject(currentUser))" />
          </td>
          <td class="px-4 py-2 break-words" v-if="(helpers.userCanManageProject(currentUser))">
            <TextInput type="number" class="mt-1 block w-full" v-model="form.styles[index].retailprice"
              :disabled="(!helpers.userCanManageProject(currentUser))" />
          </td>
          <td class="px-4 py-2 break-words">
            <Checkbox id="on-carrier-account" class="ml-1" :disabled="(!helpers.userCanManageProject(currentUser))"
              v-model:checked="form.styles[index].is_billable_sample" />
          </td>
          <td class="px-4 py-2 break-words">
            <Checkbox id="taxable" class="ml-1" :disabled="(!helpers.userCanManageProject(currentUser))"
              v-model:checked="form.styles[index].is_taxable" />
          </td>
          <td class="px-4 py-2 break-words">
            <span class="fa fa-trash mr-2 cursor-pointer" title="Delete Style" @click="deleteRow(index)"
              v-if="(!hideSave && helpers.userCanManageProject(currentUser))"></span>
          </td>
        </tr>
      </tbody>
    </table>
    <p v-if="form.styles.length === 0">No records...</p>
    <div v-if="Object.keys(form.errors).some((key) => key.startsWith('styles.'))" class="mt-2 text-sm text-red-600">
      <p v-for="(message, key) in form.errors" :key="key" v-show="String(key).startsWith('styles.')">
        {{ message }}
      </p>
    </div>
    <PrimaryButton type="button" class="mt-2" @click.prevent="addNewPricingRow()" v-if="!hideSave">Add new row
    </PrimaryButton>
  </div>
  <div class="px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md">
    <div class="sm:grid grid-cols-6 gap-6" v-if="!helpers.userIsVendor(currentUser)">
      <!-- Actual Shipping Cost -->
      <div class="col-span-6 sm:col-span-4">
        <div class="grid grid-cols-2 gap-2">
          <div>
            <InputLabel for="shipping-charges" value="Actual Shipping Cost $:" />
            <DisabledInput id="shipping-charges" class="mt-1 block w-full" :value="form.pricing.shipping_cost" />
          </div>
          <div>
            <InputLabel for="shipping-to-client" :value="`${pricingDeliveryBilledLabel} $:`" />
            <TextInput id="shipping-to-client" type="number" class="mt-1 block w-full"
              v-model="form.pricing.client_shipping_cost"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))" />
            <InputError class="mt-2" :message="form.errors['style.pricing.client_shipping_cost']" />
          </div>
        </div>
      </div>
      <!-- Taxes -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="taxes" value="Taxes" />
        <SelectInput v-model="form.pricing.taxes" id="taxes"
          :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
          class="mt-1 disabled:bg-gray-50">
          <option :value="0">Please select</option>
          <option v-for="tax in taxes" :value="tax.tax_amount" v-text="`${tax.tax_name} (${tax.tax_amount * 100}%)`">
          </option>
        </SelectInput>
        <InputError class="mt-2" :message="form.errors['style.pricing.taxes']" />
      </div>
      <!-- Purchase Order Total -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="purchase-order-total" value="Purchase Order Total $:" />
        <TextInput id="purchase-order-total" type="number" class="mt-1 block w-full" v-model="form.pricing.prjquote"
          :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))" />
        <InputError class="mt-2" :message="form.errors['style.pricing.prjquote']" />
      </div>
      <!-- Job Costing -->
      <div
        class="col-span-6 sm:col-span-4"
        v-if="isButtonVisible('project.edit.pricing.job_costing')"
      >
        <InputLabel for="purchase-order-total" value="Job Costing $:" />
        <PrimaryButton type="button" class="mr-2" @click="showEstimatedBreakdown = !showEstimatedBreakdown">
          <span v-if="!showEstimatedBreakdown">View</span><span v-else>Hide</span>&nbsp;Breakdown
        </PrimaryButton>
        <span v-text="calculateEstimatedUnitCost()"></span>
        <div v-if="showEstimatedBreakdown" class="mt-2">
          <p>Project Estimated Unit Cost</p>
          <!-- Pattern Set-up -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="pattern-setup" value="Pattern Set-Up $:" />
            <TextInput id="pattern-setup" type="number" class="mt-1 block w-full"
              v-model="form.estimated_unit_cost.ptrnsetup"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))" />
            <InputError class="mt-2" :message="form.errors['estimated_unit_cost.ptrnsetup']" />
          </div>
          <!-- Grading Set-up -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="grading-setup" value="Grading Set-Up $:" />
            <TextInput id="grading-setup" type="number" class="mt-1 block w-full"
              v-model="form.estimated_unit_cost.grdngsetup"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))" />
            <InputError class="mt-2" :message="form.errors['estimated_unit_cost.grdngsetup']" />
          </div>
          <!-- Sample Fee Set-up -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="sample-setup" value="Sample Fee Set-Up $:" />
            <TextInput id="sample-setup" type="number" class="mt-1 block w-full"
              v-model="form.estimated_unit_cost.smplefeesetup"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))" />
            <InputError class="mt-2" :message="form.errors['estimated_unit_cost.smplefeesetup']" />
          </div>
          <!-- Fabric -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="fabric" value="Fabric $:" />
            <TextInput id="fabric" type="number" class="mt-1 block w-full" v-model="form.estimated_unit_cost.fabric"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))" />
            <InputError class="mt-2" :message="form.errors['estimated_unit_cost.fabric']" />
          </div>
          <!-- Trim -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="trim" value="Trim $:" />
            <TextInput id="trim" type="number" class="mt-1 block w-full" v-model="form.estimated_unit_cost.trimfee"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))" />
            <InputError class="mt-2" :message="form.errors['estimated_unit_cost.trimfee']" />
          </div>
          <!-- Labor -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="labor" value="Labor $:" />
            <TextInput id="labor" type="number" class="mt-1 block w-full" v-model="form.estimated_unit_cost.labour"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))" />
            <InputError class="mt-2" :message="form.errors['estimated_unit_cost.labour']" />
          </div>
          <!-- Duty -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="duty" value="Duty $:" />
            <TextInput id="duty" type="number" class="mt-1 block w-full" v-model="form.estimated_unit_cost.duty"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))" />
            <InputError class="mt-2" :message="form.errors['estimated_unit_cost.duty']" />
          </div>
          <!-- Freight -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="frieght" value="Freight $:" />
            <TextInput id="frieght" type="number" class="mt-1 block w-full" v-model="form.estimated_unit_cost.frieght"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))" />
            <InputError class="mt-2" :message="form.errors['estimated_unit_cost.frieght']" />
          </div>
          <!-- Other -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="other" value="Other $:" />
            <TextInput id="other" type="number" class="mt-1 block w-full" v-model="form.estimated_unit_cost.other"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))" />
            <InputError class="mt-2" :message="form.errors['estimated_unit_cost.other']" />
          </div>
          <!-- Total -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Total $:" />
            <span v-text="calculateEstimatedUnitCost()"></span>
          </div>
          <hr class="mt-2">
        </div>
      </div>
      <!-- Project Completion Cost -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="project-completion-cost" value="Project Completion Cost $:" />
        <TextInput id="project-completion-cost" type="number" class="mt-1 block w-full"
          v-model="form.pricing.prj_completioncost"
          :disabled="isGarmentColorPricingView || (!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))" />
        <InputError class="mt-2" :message="form.errors['style.pricing.prj_completioncost']" />
      </div>
      <!-- Project Estimated Profit -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="project-estimated-profit" value="Project Estimated Profit $:" />
        <TextInput id="project-estimated-profit" type="number" class="mt-1 block w-full"
          v-model="form.pricing.prj_est_profit" :disabled="true" />
        <InputError class="mt-2" :message="form.errors['style.pricing.prj_est_profit']" />
      </div>
    </div>
  </div>
  <div
    class="flex items-center justify-center px-4 py-3 bg-gray-50 text-right sm:px-6 shadow sm:rounded-bl-md sm:rounded-br-md sticky bottom-0">
    <PrimaryButton @click.prevent="saveForm()" :disabled="saving" v-if="hideSave === false">Save</PrimaryButton>
  </div>
</template>
